import React, { useRef, useState } from 'react';
import {
  FileArchive, Upload, Loader2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, ExternalLink,
} from 'lucide-react';
import api from '@/lib/axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Bulk-Import dialog: accepts a single ZIP with multiple iMikel CSV/XLSX exports.
 * Recognised filenames (case-insensitive): schueler, lehrer, faecher, belegungen,
 * zahlungspflichtige, raeume, bank, rabatte, probestunden.
 *
 * Flow: upload → preview (lists recognised + ignored) → commit (dispatches per file).
 */
export default function BulkImportDialog({ open, onOpenChange, tenant, onImported }) {
  const [phase, setPhase] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const reset = () => {
    setPhase('upload'); setPreview(null); setResult(null); setFile(null); setError(null);
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const runPreview = async (f) => {
    if (!f) return;
    setFile(f); setError(null); setPhase('previewing');
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await api.post('/imports/bulk/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setPhase('confirm');
    } catch (err) {
      setError(err.response?.data?.detail || 'ZIP konnte nicht gelesen werden');
      setPhase('upload');
    }
  };

  const runCommit = async () => {
    setPhase('committing'); setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('tenant_id', tenant.id);
      const res = await api.post('/imports/bulk/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setPhase('done');
      toast.success(`Bulk-Import fertig: ${res.data.results?.length || 0} Datei(en) verarbeitet`);
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import fehlgeschlagen');
      setPhase('confirm');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="bulk-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArchive className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            iMikel Bulk-Import (ZIP)
          </DialogTitle>
          <DialogDescription>
            Mandant: <span className="font-semibold">{tenant?.name}</span> ({tenant?.slug})
          </DialogDescription>
        </DialogHeader>

        {phase === 'upload' && (
          <div className="py-3">
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-[12px] text-amber-900">
              <p className="font-semibold mb-1 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Erwartete Dateinamen im ZIP
              </p>
              <p className="leading-relaxed">
                <span className="font-mono">schueler.csv</span>,&nbsp;
                <span className="font-mono">lehrer.csv</span>,&nbsp;
                <span className="font-mono">faecher.xlsx</span>,&nbsp;
                <span className="font-mono">belegungen.csv</span>,&nbsp;
                <span className="font-mono">zahlungspflichtige.csv</span>,&nbsp;
                <span className="font-mono">raeume.csv</span>,&nbsp;
                <span className="font-mono">bank.csv</span>,&nbsp;
                <span className="font-mono">rabatte.csv</span>,&nbsp;
                <span className="font-mono">probestunden.csv</span>
              </p>
              <p className="mt-1 text-[11px]">
                Andere Dateien werden ignoriert. Vorlage / FileMaker-Script-Anleitung siehe{' '}
                <span className="font-mono">/app/memory/IMIKEL_EXPORT_GUIDE.md</span>.
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".zip"
              className="hidden"
              data-testid="bulk-file-input"
              onChange={(e) => runPreview(e.target.files?.[0])}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              className="w-full"
              data-testid="bulk-pick-file"
            >
              <Upload className="w-4 h-4 mr-2" /> ZIP wählen…
            </Button>
            {error && (
              <p className="mt-3 text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {error}
              </p>
            )}
          </div>
        )}

        {phase === 'previewing' && <Loading text="ZIP wird gelesen…" />}

        {phase === 'confirm' && preview && (
          <div data-testid="bulk-confirm">
            <p className="text-sm text-slate-600 mb-3">
              <span className="font-semibold">{preview.total_files}</span> Datei(en) im ZIP
              gefunden, davon <span className="font-semibold text-emerald-700">{preview.recognised.length}</span> erkannt.
            </p>
            {preview.recognised.length > 0 && (
              <div className="border rounded-md overflow-hidden mb-3">
                <div className="bg-emerald-50 border-b p-2 text-[11px] font-semibold text-emerald-900 uppercase tracking-wide">
                  Erkannte Dateien
                </div>
                {preview.recognised.map((r) => (
                  <div key={r.file} className="px-3 py-2 border-b last:border-b-0 flex items-center justify-between text-sm">
                    <span className="font-mono">{r.file}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      → {r.target}{r.note ? ` (${r.note})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {preview.ignored.length > 0 && (
              <div className="border rounded-md overflow-hidden mb-3">
                <div className="bg-slate-50 border-b p-2 text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                  Ignoriert (Name nicht erkannt)
                </div>
                {preview.ignored.map((n) => (
                  <div key={n} className="px-3 py-1 text-xs font-mono text-slate-500">{n}</div>
                ))}
              </div>
            )}
            {error && (
              <p className="mb-3 text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {error}
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setPhase('upload')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
              </Button>
              <Button
                onClick={runCommit}
                disabled={preview.recognised.length === 0}
                data-testid="bulk-commit"
              >
                Import starten
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {phase === 'committing' && <Loading text="Bulk-Import läuft — kann 1-2 Minuten dauern…" />}

        {phase === 'done' && result && (
          <div className="py-2" data-testid="bulk-done">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
              <CheckCircle2 className="w-5 h-5" />
              Import abgeschlossen
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {result.results.map((r, i) => (
                <div key={i} className={[
                  'border rounded-md p-3 text-sm',
                  r.error ? 'border-red-200 bg-red-50'
                    : r.skipped ? 'border-amber-200 bg-brand-red/5'
                      : 'border-emerald-200 bg-emerald-50',
                ].join(' ')}>
                  <p className="font-mono text-xs font-semibold mb-1">{r.file}</p>
                  {r.error && <p className="text-red-700 text-xs">Fehler: {r.error}</p>}
                  {r.skipped && <p className="text-amber-800 text-xs">{r.skipped}</p>}
                  {r.stats && (
                    <pre className="text-[11px] text-emerald-900 whitespace-pre-wrap">
                      {Object.entries(r.stats)
                        .filter(([k]) => !['dry_run', 'sample_created', 'sample_updated',
                                            'missing_student_samples', 'samples'].includes(k))
                        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                        .join('\n')}
                    </pre>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => close(false)}>Schließen</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Loading = ({ text }) => (
  <div className="py-10 flex flex-col items-center gap-2 text-slate-500">
    <Loader2 className="w-7 h-7 animate-spin" />
    <p className="text-sm">{text}</p>
  </div>
);
